import React, { forwardRef, useLayoutEffect, useRef, useState } from 'react';
import { readOnlyAction } from 'contexts/adminAccess';
import { MenuItem } from '@material-ui/core';
import { useDataProvider, useNotify } from 'react-admin';
import moment from 'moment';

export const ExportAccountDataActionField = readOnlyAction(
  forwardRef<HTMLLIElement, any>((props, ref) => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const { record } = props;
    const [loading, setLoading] = useState(false);
    const mountedRef = useRef(false);
    const runningRef = useRef(false);
    const operationRef = useRef(0);
    const timeoutRef = useRef<number | null>(null);

    useLayoutEffect(() => {
      mountedRef.current = true;
      setLoading(false);
      return () => {
        mountedRef.current = false;
        runningRef.current = false;
        operationRef.current += 1;
        if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      };
    }, [record?.id]);

    const handleClick = async () => {
      if (!mountedRef.current || runningRef.current) return;
      runningRef.current = true;
      const operation = ++operationRef.current;
      const accountName = record?.name || record?.email || '';
      const isCurrent = () => mountedRef.current && operationRef.current === operation;
      const finish = () => {
        if (!isCurrent()) return;
        runningRef.current = false;
        setLoading(false);
      };
      setLoading(true);

      const poll = async (executionArn: string) => {
        if (!isCurrent()) return;
        timeoutRef.current = null;
        try {
          const {
            data: { status, downloadURL },
          } = await dataProvider.get('exportReportStatus', {
            arn: executionArn,
          });
          if (!isCurrent()) return;
          if (status === 'RUNNING') {
            timeoutRef.current = window.setTimeout(() => poll(executionArn), 5000);
            return;
          }
          if (status === 'SUCCEEDED' && downloadURL) {
            const response = await fetch(downloadURL);
            if (!isCurrent()) return;
            if (!response.ok) throw new Error(`Failed to download report (${response.status}).`);
            const text = await response.text();
            if (!isCurrent()) return;
            const url = window.URL.createObjectURL(new Blob([text]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${accountName}_${moment().format()}.html`);
            document.body.appendChild(link);
            try {
              link.click();
            } finally {
              link.remove();
              window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
            }
          }
          finish();
        } catch (error) {
          if (!isCurrent()) return;
          finish();
          notify(error?.message || 'Failed to export account data');
        }
      };

      try {
        const {
          data: { executionArn },
        } = await dataProvider.get('exportDebtor', { id: record.id });
        if (!isCurrent()) return;
        timeoutRef.current = window.setTimeout(() => poll(executionArn), 5000);
      } catch (error: any) {
        if (!isCurrent()) return;
        finish();
        notify(error?.message || 'Failed to export account data');
      }
    };
    return (
      <MenuItem ref={ref} disabled={loading} onClick={handleClick}>
        Export account data
      </MenuItem>
    );
  }),
  'canReadBilling'
);
